/* eslint-disable no-debugger */
/* eslint-disable no-param-reassign */
import {
  StateCreator, UseBoundStore, StoreApi, create,
} from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { subscribeWithSelector } from 'zustand/middleware';
import { v4 as uuid } from 'uuid';
import {
  createImage, FabricObject, collectViewportBoundsFromRight, collectViewportBoundsFromBottomLeft,
  ElementName, Operation, Group, ActiveSelection, Point, createVideo, createDynamicPicture, getElementOptions, showAllObjects,
  AlignMode, getShapePositionByScreenBound, RenderCursor, createContainerElements, DeltaType,
  isRealElement, createElement, getViewPortVisibleArea, Canvas,
  IImage,
  FabricImage,
  ImageProps,
  IVideo,
  VideoProperties,
  ImageProperties,
  IDynamicPicture,
  DynamicPictureProperties,
  createAudio,
} from '@meitu/whee-infinite-canvas';
import useTextEditSote from '$/store/useTextEditSote';
import { useLanguageStore } from '$/store/languageStore';
import useCursorStore, {
  setSelectCursor, setActiveCursor, CursorType, setFrameCursor,
} from '$/store/useCursorStore';
import useAIEditImageStore from '$/store/useAIEditImageStore';
import { getUrlSearchParams } from '$/utils';
import { getDefaultName } from '$/utils/canvasUtil';
import { hasTaskByLayerId } from '$/store/useLayerTaskStore';
// eslint-disable-next-line import/no-cycle
import { saveLayerData, deleteLayerData } from '$/pages/RoboneoCanvas/components/InfiniteCanvas/layerData';
import { submitAddHistroy, submitModifyHistory } from '$/utils/canvasHistory';
import { mtstatTrack } from '$/utils/mtstat';
import { handleImageUrl } from '$/utils/image';
import { copyFabricObjectToClipboard } from '$/utils/copyUtil';
import { LyricLine, parseLyrics } from '$/utils/audioUtil';
import useCanvasStore from './useCanvasStore';
// eslint-disable-next-line import/no-cycle
import useChatStore from './useChatStore';
import type { Write, WithImmer } from './immer.d';
import type { StoreSubscribeWithSelector } from './subscribe.d';
import useThemeStore from './useThemeStore';

export const EXPORT_SVG_LIMIT = 1024 * 4;

interface TCreateObjectState {
  currentObjects: FabricObject[];
  copyObjects: null | FabricObject[];
  currentObjectIds(): string[];
  exportDisabled(): boolean;
  exportSvgDisabled(): boolean;
}

const canvasSlice: StateCreator<TCreateObjectState, [
  ['zustand/immer', never],
  ['zustand/subscribeWithSelector', never],
]> = (_, get) => ({
  currentObjects: [],
  copyObjects: null,
  currentObjectIds() {
    return get().currentObjects.map((item) => item._id_);
  },
  exportDisabled() {
    const allVideoOrDynamicPicture = get().currentObjects.every((item) => item._name_ === ElementName.VIDEO || item._name_ === ElementName.DYNAMIC_PICTURE);
    return !get().currentObjects.length || allVideoOrDynamicPicture;
  },
  exportSvgDisabled() {
    const filterList = get().currentObjects.filter((item) => item._name_ !== ElementName.VIDEO);
    if (filterList.length === 0) {
      return true;
    }
    const scaleWidth = filterList[0].getScaledWidth();
    const scaleHeight = filterList[0].getScaledHeight();
    // 只支持单张 1024 * 1024
    return filterList.length !== 1 || scaleWidth > EXPORT_SVG_LIMIT || scaleHeight > EXPORT_SVG_LIMIT;
  },
});

const useCanvasObjectStore: UseBoundStore<Write<WithImmer<StoreApi<TCreateObjectState>>, StoreSubscribeWithSelector<TCreateObjectState>>> = create<TCreateObjectState>()(immer(subscribeWithSelector(canvasSlice)));

export default useCanvasObjectStore;

// 获取元素位置
export function getObjectsPosition(objects: FabricObject[]) {
  if (!objects || objects.length === 0) {
    return {
      left: 0,
      top: 0,
      width: 0,
      height: 0,
    };
  }

  let minLeft = Infinity;
  let minTop = Infinity;
  let maxRight = -Infinity;
  let maxBottom = -Infinity;

  objects.forEach((item) => {
    const {
      left,
      top,
      width,
      height,
    } = item.getBoundingRect();
    const right = left + width;
    const bottom = top + height;
    if (left < minLeft) minLeft = left;
    if (top < minTop) minTop = top;
    if (right > maxRight) maxRight = right;
    if (bottom > maxBottom) maxBottom = bottom;
  });

  return {
    left: minLeft,
    top: minTop,
    width: Math.max(0, maxRight - minLeft),
    height: Math.max(0, maxBottom - minTop),
  };
}

export function setCurrentObjects(objects: FabricObject[]) {
  const { editImageMode } = useAIEditImageStore.getState();
  // 编辑状态下，清空当前选中的图层
  const currentObjects = editImageMode !== null ? [] : objects;
  useCanvasObjectStore.setState(() => ({ currentObjects }));
}

export function setCopyObjects(objects: FabricObject[] | null) {
  useCanvasObjectStore.setState(() => ({
    copyObjects: objects,
  }));
}

export interface InsertImageParams {
  left?: number;
  top?: number;
  url: string;
  watermarkUrl?: string;
  uri: string;
  taskId?: string;
  recordId?: string;
  name?: string;
  suffix?: string | null;
  width?: number; // 图片宽度
  height?: number; // 图片高度
  insertPos?: 'leftBottom' | 'right' | 'point'; // 插入位置 'leftBottom' 左下角，'right' 右侧，'point' 鼠标位置
  submitHistory?: boolean;
  target?: FabricObject;
  isContinueMatch?: boolean; // 脱离可视区域是否继续匹配
  scaleX?: number;
  scaleY?: number;
  extraData?: Record<string, any>;
}

export interface InsertVideoParams extends InsertImageParams {
  coverUrl: string;
  watermarkCoverUrl?: string;
  scaleX?: number;
  scaleY?: number;
}

export interface InsertAudioParams extends InsertImageParams {
  lyrics?: string;
}

export type KeyDirection = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';

/**
 * 图片插入时优先使用真实水印地址；水印地址与原图相同时再通过云处理参数叠加默认水印。
 */
function resolveInsertImageRenderUrl(url: string, watermarkUrl?: string): string {
  const displayUrl = watermarkUrl || url;
  return handleImageUrl(displayUrl, { showWatermark: Boolean(watermarkUrl && watermarkUrl === url) });
}

/**
 * 插入图片
 * @export
 * @param {InsertImageParams} {
 *   url,
 *   uri,
 *   name = '',
 * }
 */
export async function insertAudio({
  left,
  top,
  url,
  watermarkUrl,
  lyrics,
  taskId,
  recordId,
  name = '',
  width,
  suffix = 'mp3',
  insertPos = 'leftBottom',
  submitHistory = true,
  isContinueMatch = false,
  extraData,
}: InsertAudioParams) {
  const { render } = useCanvasStore.getState();

  const el = await createAudio('', {
    left,
    top,
    width: width || 348,
    source: watermarkUrl || url,
    title: name,
    lyrics: lyrics ? parseLyrics(lyrics) : [],
    showLyrics: !!lyrics,
    maxLyricsLines: 5,
    backgroundColor: 'rgba(43, 43, 45, 1)',
    // backgroundColor: theme === 'light' ? 'rgba(17,18,20,0.06)' : 'rgba(240,245,255,0.08)',
    lyricsStyle: {
      fontSize: 12,
      color: 'rgba(247,248,250,0.28)',
      // color: theme === 'light' ? 'rgba(17,18,20,0.25)' : 'rgba(247,248,250,0.28)',
      highlightFontSize: 14,
      highlightColor: 'rgba(247,248,250,0.96)',

      // highlightColor: theme === 'light' ? 'rgba(17,17,20,1)' : 'rgba(247,248,250,0.96)',
      lineSpacing: 10,
    },
    backgroundVideo:
    'https://titan-h5.meitu.com/whee/assets/website/canvas-test/canvas-audio-background.webm',
    titleTimeStyle: {
      fontSize: 15,
      titleColor: 'rgba(247,248,250,0.96)',
      timeColor: 'rgba(247,248,250,0.96)',
      totalTimeColor: 'rgba(247,248,250,0.42)',
      // titleColor: theme === 'light' ? 'rgba(17,17,20,1)' : 'rgba(247,248,250,0.96)',
      // timeColor: theme === 'light' ? 'rgba(17,17,20,1)' : 'rgba(247,248,250,0.96)',
      // totalTimeColor: theme === 'light' ? 'rgba(17,18,20,0.45)' : 'rgba(247,248,250,0.42)',
    },
    waveformStyle: {
      height: 40,
      waveColor: 'rgba(255,255,255,0.12)',
      progressColor: 'rgba(255,255,255,0.6)',
      // waveColor: theme === 'light' ? 'rgba(17,18,20,0.10)' : 'rgba(255,255,255,0.12)',
      // progressColor: theme === 'light' ? 'rgba(17,18,20,0.24)' : 'rgba(255,255,255,0.6)',
      barWidth: 2,
      barGap: 2,
      barRadius: 999,
    },
    renderStrategy: 'layered',
    perfHints: {
      dynamicFps: 30,
      lyricsFps: 2,
      timeFps: 10,
      waveformFps: 30,
    },
    _custom_data_history_: {
      url,
      suffix,
      taskId,
      recordId,
      watermarkUrl,
      extraData,
    },
    _shape_name_: name || getDefaultName('audio'),
  });
  // 如果画布中没有元素,插在画布正中心
  if (!render?._FC.getObjects().length && insertPos !== 'point') {
    render?.addToViewPortCenterByArrangement(el);
  }
  else if (insertPos) {
    switch (insertPos) {
      case 'point': {
        el.set({
          left: (left ?? 0) + el.width / 2,
          top: (top ?? 0) + el.height / 2,
        });
        render?.add(el);
        break;
      }
      case 'right': {
        const { currentObjects } = useCanvasObjectStore.getState();
        // centerX, centerY 是画布可视区域的中心点,作为兜底,但是当insertPos为right时候,尽量传left和top,只有传left和top才是最准的
        const { centerX, centerY } = getViewPortVisibleArea(render?._FC as Canvas);
        const locationParams = {
          width: el.width,
          height: el.height,
          left: left ?? (currentObjects.length > 0 ? (currentObjects[0].left - currentObjects[0].width / 2) : centerX),
          top: top ?? (currentObjects.length > 0 ? (currentObjects[0].top - currentObjects[0].height / 2) : centerY),
        };
        const data = collectViewportBoundsFromRight.call(render!, locationParams, 20, 0, isContinueMatch);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
        break;
      }
      case 'leftBottom': {
        const data = collectViewportBoundsFromBottomLeft.call(render!, { width: el.width, height: el.height }, 0, 20, false);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
        break;
      }
    }
  }

  // 编辑状态新插入的图片，不要选中和聚焦
  if (useAIEditImageStore.getState().editImageMode === null) {
    render?._FC.setActiveObject(el);
    suitableLoaction([el], 0.4);
  }
  setSelectCursor();
  saveLayerData(null, [el]);
  submitHistory && submitAddHistroy();
  return el as FabricObject;
}

/**
 * 插入图片
 * @export
 * @param {InsertImageParams} {
 *   url,
 *   uri,
 *   name = '',
 * }
 */
export async function insertImage({
  left,
  top,
  url,
  watermarkUrl,
  taskId,
  recordId,
  name = '',
  width,
  height,
  suffix = 'png',
  insertPos = 'leftBottom',
  submitHistory = true,
  isContinueMatch = false,
  scaleX = 1,
  scaleY = 1,
  extraData,
}: InsertImageParams) {
  const { render } = useCanvasStore.getState();

  const el = await createImage('', {
    left,
    top,
    width,
    height,
    src: resolveInsertImageRenderUrl(url, watermarkUrl),
    _custom_data_history_: {
      url,
      suffix,
      taskId,
      recordId,
      watermarkUrl,
      extraData,
    },
    _shape_name_: name || getDefaultName('image'),
    angle: 0,
    scaleX,
    scaleY,
  });
  // 如果画布中没有元素,插在画布正中心
  if (!render?._FC.getObjects().length && insertPos !== 'point') {
    render?.addToViewPortCenterByArrangement(el);
  }
  else if (insertPos) {
    switch (insertPos) {
      case 'point': {
        el.set({
          left: (left ?? 0) + el.width / 2,
          top: (top ?? 0) + el.height / 2,
        });
        render?.add(el);
        break;
      }
      case 'right': {
        const { currentObjects } = useCanvasObjectStore.getState();
        // centerX, centerY 是画布可视区域的中心点,作为兜底,但是当insertPos为right时候,尽量传left和top,只有传left和top才是最准的
        const { centerX, centerY } = getViewPortVisibleArea(render?._FC as Canvas);
        const locationParams = {
          width: el.width * scaleX,
          height: el.height * scaleY,
          left: left ?? (currentObjects.length > 0 ? (currentObjects[0].left - currentObjects[0].width / 2) : centerX),
          top: top ?? (currentObjects.length > 0 ? (currentObjects[0].top - currentObjects[0].height / 2) : centerY),
        };
        const data = collectViewportBoundsFromRight.call(render!, locationParams, 20, 0, isContinueMatch);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
        break;
      }
      case 'leftBottom': {
        const data = collectViewportBoundsFromBottomLeft.call(render!, { width: el.width, height: el.height }, 0, 20, false);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
        break;
      }
    }
  }

  // 编辑状态新插入的图片，不要选中和聚焦
  if (useAIEditImageStore.getState().editImageMode === null) {
    render?._FC.setActiveObject(el);
    suitableLoaction([el], 0.4);
  }
  setSelectCursor();
  saveLayerData(null, [el]);
  submitHistory && submitAddHistroy();
  return el;
}

/**
 * 异步插入图片
 * @param params 图片参数
 * @returns 图片元素
 */
export function insertImageAsync({
  left,
  top,
  url,
  watermarkUrl,
  taskId,
  recordId,
  name = '',
  width,
  height,
  suffix = 'png',
  insertPos = 'leftBottom',
  submitHistory = true,
  isContinueMatch = false,
  scaleX = 1,
  scaleY = 1,
  extraData,
}: InsertImageParams) {
  const { render } = useCanvasStore.getState();

  const el = createImageAsync('', {
    left,
    top,
    width,
    height,
    src: resolveInsertImageRenderUrl(url, watermarkUrl),
    _custom_data_history_: {
      url,
      suffix,
      taskId,
      recordId,
      watermarkUrl,
      extraData,
    },
    _shape_name_: name || getDefaultName('image'),
    angle: 0,
    scaleX,
    scaleY,
  });
  // 如果画布中没有元素,插在画布正中心
  if (!render?._FC.getObjects().length && insertPos !== 'point') {
    render?.addToViewPortCenterByArrangement(el);
  }
  else if (insertPos) {
    switch (insertPos) {
      case 'point': {
        el.set({
          left: (left ?? 0) + el.width / 2,
          top: (top ?? 0) + el.height / 2,
        });
        render?.add(el);
        break;
      }
      case 'right': {
        const { currentObjects } = useCanvasObjectStore.getState();
        // centerX, centerY 是画布可视区域的中心点,作为兜底,但是当insertPos为right时候,尽量传left和top,只有传left和top才是最准的
        const { centerX, centerY } = getViewPortVisibleArea(render?._FC as Canvas);
        const locationParams = {
          width: el.width * scaleX,
          height: el.height * scaleY,
          left: left ?? (currentObjects.length > 0 ? (currentObjects[0].left - currentObjects[0].width / 2) : centerX),
          top: top ?? (currentObjects.length > 0 ? (currentObjects[0].top - currentObjects[0].height / 2) : centerY),
        };
        const data = collectViewportBoundsFromRight.call(render!, locationParams, 20, 0, isContinueMatch);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
        break;
      }
      case 'leftBottom': {
        const data = collectViewportBoundsFromBottomLeft.call(render!, { width: el.width, height: el.height }, 0, 20, false);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
        break;
      }
    }
  }

  // 编辑状态新插入的图片，不要选中和聚焦
  if (useAIEditImageStore.getState().editImageMode === null) {
    render?._FC.setActiveObject(el);
    suitableLoaction([el], 0.4);
  }
  setSelectCursor();
  saveLayerData(null, [el]);
  submitHistory && submitAddHistroy();
  return el;
}

/**
 * 插入动图
 *
 * @export
 * @param {InsertVideoParams} {
 *   url,
 *   uri,
 *   taskId,
 *   left,
 *   top,
 *   width,
 *   height,
 *   coverUrl,
 *   name,
 *   insertPos = 'leftBottom',
 *   submitHistory = true,
 *   isContinueMatch = false,
 * }
 * @return {*}
 */
export async function insertDynamicImage({
  url,
  watermarkUrl,
  taskId,
  recordId,
  left,
  top,
  width,
  height,
  coverUrl,
  watermarkCoverUrl,
  name,
  insertPos = 'leftBottom',
  submitHistory = true,
  isContinueMatch = false,
  scaleX = 1,
  scaleY = 1,
  extraData,
}: InsertVideoParams) {
  const { render } = useCanvasStore.getState();
  const el = await createDynamicPicture('', {
    left,
    top,
    width,
    height,
    source: watermarkUrl || url,
    src: watermarkUrl || url,
    cover: watermarkCoverUrl || coverUrl,
    frameType: 'dynamicPicture',
    _shape_name_: name || getDefaultName('image'),
    scaleX,
    scaleY,
    _custom_data_history_: {
      coverUrl,
      watermarkUrl,
      url,
      taskId,
      recordId,
      watermarkCoverUrl,
      extraData,
    },
  });

  // 如果画布中没有元素,插在画布正中心
  if (!render?._FC.getObjects().length && insertPos !== 'point') {
    render?.addToViewPortCenterByArrangement(el);
  } else {
    switch (insertPos) {
      case 'point': {
        el.set({
          left: (left ?? 0) + el.width / 2,
          top: (top ?? 0) + el.height / 2,
        });
        render?.add(el);
        break;
      }
      case 'right': {
        const { currentObjects } = useCanvasObjectStore.getState();
        // centerX, centerY 是画布可视区域的中心点,作为兜底,但是当insertPos为right时候,尽量传left和top,只有传left和top才是最准的
        const { centerX, centerY } = getViewPortVisibleArea(render?._FC as Canvas);
        const locationParams = {
          width: el.width * scaleX,
          height: el.height * scaleY,
          left: left || (currentObjects.length > 0 ? (currentObjects[0].left - currentObjects[0].width / 2) : centerX),
          top: top || (currentObjects.length > 0 ? (currentObjects[0].top - currentObjects[0].height / 2) : centerY),
        };
        const data = collectViewportBoundsFromRight.call(render!, locationParams, 20, 0, isContinueMatch);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
        break;
      }
      case 'leftBottom': {
        const data = collectViewportBoundsFromBottomLeft.call(render!, { width: el.width, height: el.height }, 0, 20, false);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
      }
    }
  }

  // 编辑状态新插入的视频，不要选中和聚焦
  if (useAIEditImageStore.getState().editImageMode === null) {
    render?._FC.setActiveObject(el);
    suitableLoaction([el], 0.4);
  }

  setSelectCursor();
  saveLayerData(null, [el]);
  submitHistory && submitAddHistroy();
  return el;
}

export function insertDynamicImageAsync({
  url,
  watermarkUrl,
  taskId,
  recordId,
  left,
  top,
  width,
  height,
  coverUrl,
  watermarkCoverUrl,
  name,
  insertPos = 'leftBottom',
  submitHistory = true,
  isContinueMatch = false,
  scaleX = 1,
  scaleY = 1,
  extraData,
}: InsertVideoParams) {
  const { render } = useCanvasStore.getState();
  const el = createDynamicPictureAsync('', {
    left,
    top,
    width,
    height,
    source: watermarkUrl || url,
    src: watermarkUrl || url,
    cover: watermarkCoverUrl || coverUrl,
    frameType: 'dynamicPicture',
    _shape_name_: name || getDefaultName('image'),
    scaleX,
    scaleY,
    _custom_data_history_: {
      coverUrl,
      watermarkUrl,
      url,
      taskId,
      recordId,
      watermarkCoverUrl,
      extraData,
    },
  });

  // 如果画布中没有元素,插在画布正中心
  if (!render?._FC.getObjects().length && insertPos !== 'point') {
    render?.addToViewPortCenterByArrangement(el);
  } else {
    switch (insertPos) {
      case 'point': {
        el.set({
          left: (left ?? 0) + el.width / 2,
          top: (top ?? 0) + el.height / 2,
        });
        render?.add(el);
        break;
      }
      case 'right': {
        const { currentObjects } = useCanvasObjectStore.getState();
        // centerX, centerY 是画布可视区域的中心点,作为兜底,但是当insertPos为right时候,尽量传left和top,只有传left和top才是最准的
        const { centerX, centerY } = getViewPortVisibleArea(render?._FC as Canvas);
        const locationParams = {
          width: el.width * scaleX,
          height: el.height * scaleY,
          left: left || (currentObjects.length > 0 ? (currentObjects[0].left - currentObjects[0].width / 2) : centerX),
          top: top || (currentObjects.length > 0 ? (currentObjects[0].top - currentObjects[0].height / 2) : centerY),
        };
        const data = collectViewportBoundsFromRight.call(render!, locationParams, 20, 0, isContinueMatch);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
        break;
      }
      case 'leftBottom': {
        const data = collectViewportBoundsFromBottomLeft.call(render!, { width: el.width, height: el.height }, 0, 20, false);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
      }
    }
  }

  // 编辑状态新插入的视频，不要选中和聚焦
  if (useAIEditImageStore.getState().editImageMode === null) {
    render?._FC.setActiveObject(el);
    suitableLoaction([el], 0.4);
  }

  setSelectCursor();
  saveLayerData(null, [el]);
  submitHistory && submitAddHistroy();
  return el;
}

/**
 * 插入文字
 *
 * @export
 * @return {*}
 */
export function insertText() {
  const {
    render, renderStyle, mouseCreate,
  } = useCanvasStore.getState();
  mouseCreate?.reset();
  const { activeCursor } = useCursorStore.getState();
  if (activeCursor === CursorType.Text) {
    setSelectCursor();
    return;
  }

  setActiveCursor(CursorType.Text);
  renderStyle?.setCursorStyle({
    move: RenderCursor.insertText,
    hover: RenderCursor.insertText,
    defaults: RenderCursor.insertText,
  });
  const { languageStore } = useLanguageStore.getState();
  const defaultText = languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_studio_font_default_text);
  const { defaultFontFamily } = useTextEditSote.getState();
  mouseCreate?.createText({
    defaultText,
    callback: (textObject: FabricObject) => {
      textObject?.set({
        fontSize: 120,
        fill: '#fff',
        fontFamily: defaultFontFamily.default_name || defaultFontFamily.name,
      });
      // @ts-ignore
      textObject._font_url_ = defaultFontFamily.font_file;
      return textObject;
    },
  });

  render?._FC.once('object:insert', ({ objects }) => {
    submitHistroyHandler({
      objects,
      beforHandler: () => {
        mtstatTrack('canvas_import', {
          import_path: 1,
          import_type: 'text',
          text_num: 1,
        });
      },
    });
  });
}

// 插入画布修改历史记录
const submitHistroyHandler = ({ objects, beforHandler }: { objects: FabricObject[]; beforHandler: () => void; }) => {
  const { historyPlugins } = useCanvasStore.getState();
  beforHandler();
  if (!objects) return;
  const operations: Operation[] = [];
  const operation = historyPlugins?.baseAction.getAddOperation({
    objects,
  });
  if (!operation) return;
  operations.push(operation);
  historyPlugins?.submit(operations);
  saveLayerData(null, objects);
  setSelectCursor();
};

/**
 * 插入矩形
 *
 * @export
 * @return {*}
 */
export function insertRectangle() {
  const { mouseCreate, render } = useCanvasStore.getState();
  const { theme } = useThemeStore.getState();
  mouseCreate?.reset();
  setFrameCursor();
  mouseCreate?.createRectangle({
    defaultHeight: 1024,
    defaultWidth: 1024,
    minHeight: 1,
    minWidth: 1,
    strokeColor: theme === 'dark' ? 'rgba(255,255,255,1)' : 'rgba(0,0,0,1)',
    maxHeight: 10000,
    maxWidth: 10000,
    fill: '#A4A4A4',
    callback: (val: FabricObject) => {
      val._shape_name_ = getDefaultName('rectangle');
      return val;
    },
  });
  render?._FC.once('object:insert', ({ objects }) => {
    submitHistroyHandler({
      objects,
      beforHandler: () => {
        mtstatTrack('canvas_import', {
          import_path: 1,
          import_type: 'rectangle',
          text_num: 1,
        });
      },
    });
  });
}

/**
 * 插入圆形
 *
 * @export
 * @return {*}
 */
export function insertEllipse() {
  const { mouseCreate, render } = useCanvasStore.getState();
  const { theme } = useThemeStore.getState();
  mouseCreate?.reset();
  setFrameCursor();
  mouseCreate?.createEllipse({
    defaultHeight: 1024,
    defaultWidth: 1024,
    minHeight: 1,
    minWidth: 1,
    maxHeight: 10000,
    maxWidth: 10000,
    strokeColor: theme === 'dark' ? 'rgba(255,255,255,1)' : 'rgba(0,0,0,1)',
    fill: '#A4A4A4',
    callback: (val: FabricObject) => {
      val._shape_name_ = getDefaultName('ellipse');
      return val;
    },
  });
  render?._FC.once('object:insert', ({ objects }) => {
    submitHistroyHandler({
      objects,
      beforHandler: () => {
        mtstatTrack('canvas_import', {
          import_path: 1,
          import_type: 'round',
          text_num: 1,
        });
      },
    });
  });
}

/**
 * 插入箭头
 *
 * @export
 * @return {*}
 */
export function insertArrow() {
  const { mouseCreate, render } = useCanvasStore.getState();
  mouseCreate?.reset();
  mouseCreate?.createArrow({
    defaultLength: 600,
    defaultAngle: 0,
    minHeight: 64,
    minWidth: 64,
    maxHeight: 10000,
    maxWidth: 10000,
    headSize: 10,
    strokeColor: '#A4A4A4',
    strokeWidth: 8,
    callback: (val: FabricObject) => {
      val._shape_name_ = getDefaultName('arrow');
      return val;
    },
  });
  render?._FC.once('object:insert', ({ objects }) => {
    submitHistroyHandler({
      objects,
      beforHandler: () => {
        mtstatTrack('canvas_import', {
          import_path: 1,
          import_type: 'arrow',
          text_num: 1,
        });
      },
    });
  });
}

/**
 *  插入视频
 * @export
 * @param {InsertImageParams} {
 *   url,
 *   coverUrl,
 *   uri,
 *  name,
 * }
 */
export async function insertVideo({
  url,
  watermarkUrl,
  taskId,
  recordId,
  left,
  top,
  width,
  height,
  coverUrl,
  watermarkCoverUrl,
  name,
  insertPos = 'leftBottom',
  submitHistory = true,
  isContinueMatch = false,
  scaleX = 1,
  scaleY = 1,
  extraData,
}: InsertVideoParams) {
  const { render } = useCanvasStore.getState();
  const el = await createVideo('', {
    left,
    top,
    width,
    height,
    source: watermarkUrl || url,
    cover: watermarkCoverUrl || coverUrl,
    _shape_name_: name || getDefaultName('video'),
    frameType: 'video',
    scaleX,
    scaleY,
    _custom_data_history_: {
      coverUrl,
      watermarkUrl,
      url,
      taskId,
      recordId,
      watermarkCoverUrl,
      extraData,
    },
  });
  // 如果画布中没有元素,插在画布正中心
  if (!render?._FC.getObjects().length && insertPos !== 'point') {
    render?.addToViewPortCenterByArrangement(el);
  } else {
    switch (insertPos) {
      case 'point': {
        el.set({
          left: (left ?? 0) + el.width / 2,
          top: (top ?? 0) + el.height / 2,
        });
        render?.add(el);
        break;
      }
      case 'right': {
        const { currentObjects } = useCanvasObjectStore.getState();
        // centerX, centerY 是画布可视区域的中心点,作为兜底,但是当insertPos为right时候,尽量传left和top,只有传left和top才是最准的
        const { centerX, centerY } = getViewPortVisibleArea(render?._FC as Canvas);
        const locationParams = {
          width: el.width * scaleX,
          height: el.height * scaleX,
          left: left || (currentObjects.length > 0 ? (currentObjects[0].left - currentObjects[0].width / 2) : centerX),
          top: top || (currentObjects.length > 0 ? (currentObjects[0].top - currentObjects[0].height / 2) : centerY),
        };
        const data = collectViewportBoundsFromRight.call(render!, locationParams, 20, 0, isContinueMatch);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
        break;
      }
      case 'leftBottom': {
        const data = collectViewportBoundsFromBottomLeft.call(render!, { width: el.width, height: el.height }, 0, 20, false);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
      }
    }
  }
  // 编辑状态新插入的视频，不要选中和聚焦
  if (useAIEditImageStore.getState().editImageMode === null) {
    render?._FC.setActiveObject(el);
    suitableLoaction([el], 0.4);
  }

  setSelectCursor();
  saveLayerData(null, [el]);
  submitHistory && submitAddHistroy();
  return el;
}

export function insertVideoAsync({
  url,
  watermarkUrl,
  taskId,
  recordId,
  left,
  top,
  width,
  height,
  coverUrl,
  watermarkCoverUrl,
  name,
  insertPos = 'leftBottom',
  submitHistory = true,
  isContinueMatch = false,
  scaleX = 1,
  scaleY = 1,
  extraData,
}: InsertVideoParams) {
  const { render } = useCanvasStore.getState();
  const el = createVideoAsync('', {
    left,
    top,
    width,
    height,
    source: watermarkUrl || url,
    cover: watermarkCoverUrl || coverUrl,
    _shape_name_: name || getDefaultName('video'),
    frameType: 'video',
    scaleX,
    scaleY,
    _custom_data_history_: {
      coverUrl,
      watermarkUrl,
      url,
      taskId,
      recordId,
      watermarkCoverUrl,
      extraData,
    },
  });
  // 如果画布中没有元素,插在画布正中心
  if (!render?._FC.getObjects().length && insertPos !== 'point') {
    render?.addToViewPortCenterByArrangement(el);
  } else {
    switch (insertPos) {
      case 'point': {
        el.set({
          left: (left ?? 0) + el.width / 2,
          top: (top ?? 0) + el.height / 2,
        });
        render?.add(el);
        break;
      }
      case 'right': {
        const { currentObjects } = useCanvasObjectStore.getState();
        // centerX, centerY 是画布可视区域的中心点,作为兜底,但是当insertPos为right时候,尽量传left和top,只有传left和top才是最准的
        const { centerX, centerY } = getViewPortVisibleArea(render?._FC as Canvas);
        const locationParams = {
          width: el.width * scaleX,
          height: el.height * scaleY,
          left: left || (currentObjects.length > 0 ? (currentObjects[0].left - currentObjects[0].width / 2) : centerX),
          top: top || (currentObjects.length > 0 ? (currentObjects[0].top - currentObjects[0].height / 2) : centerY),
        };
        const data = collectViewportBoundsFromRight.call(render!, locationParams, 20, 0, isContinueMatch);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
        break;
      }
      case 'leftBottom': {
        const data = collectViewportBoundsFromBottomLeft.call(render!, { width: el.width, height: el.height }, 0, 20, false);
        el.set({
          left: data.left,
          top: data.top,
        });
        render?.add(el);
      }
    }
  }
  // 编辑状态新插入的视频，不要选中和聚焦
  if (useAIEditImageStore.getState().editImageMode === null) {
    render?._FC.setActiveObject(el);
    suitableLoaction([el], 0.4);
  }

  setSelectCursor();
  saveLayerData(null, [el]);
  submitHistory && submitAddHistroy();
  return el;
}

/**
 * 获取 targets 或 当前选中元素
 *
 * @param {FabricObject[]} [targets]
 * @return {*}
 */
function mergeTargetsActiveObject(targets?: FabricObject[]) {
  const { render } = useCanvasStore.getState();
  if (!render) {
    return null;
  }
  let els = targets;
  if (!els) {
    els = render._FC.getActiveObjects();
  }

  return els;
}

/**
 * 图层移动
 * @param {FabricObject[]} [targets]
 * @return {*}
 */

export function moveActiveObject({ moveDistance, direction }: { moveDistance: number;direction: KeyDirection; }) {
  const { render } = useCanvasStore.getState();
  const activeObject = render?._FC.getActiveObject() as FabricObject;
  if (!activeObject) return;
  switch (direction) {
    case 'ArrowUp':
      activeObject.set('top', activeObject.top - moveDistance);

      break;
    case 'ArrowDown':
      activeObject.set('top', activeObject.top + moveDistance);

      break;
    case 'ArrowLeft':
      activeObject.set('left', activeObject.left - moveDistance);
      break;
    case 'ArrowRight':
      activeObject.set('left', activeObject.left + moveDistance);
      break;
    default:
      break;
  }
  activeObject.setCoords();
  render?._FC.requestRenderAll();
}

/**
 * 组合
 * @export
 * @param {FabricObject[]} [targets]
 * @return {*}
 */
export function createGroup(targets?: FabricObject[]) {
  const { render, historyPlugins } = useCanvasStore.getState();
  const els = mergeTargetsActiveObject(targets);
  if (!els || !render || els.length < 2) return;

  const inTask = hasTaskByLayerId(els.map(({ _id_ }) => _id_));
  if (inTask) return;

  const isFrameInEls = els.some((el) => el._name_ === ElementName.FRAME);
  if (isFrameInEls) return;
  mtstatTrack('group_operation_click', { type: 0 });
  const operations: Operation[] = [];
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const _shape_name_ = getDefaultName('container');
  render._FC.discardActiveObject();
  const preObjects = els.map((el) => getElementOptions.call(render, el));
  render._FC.remove(...els);
  const container = createContainerElements('', els);
  // @ts-ignore
  container._shape_name_ = _shape_name_;
  render._FC.add(container);
  render._FC.setActiveObject(container);
  render._FC.requestRenderAll();
  const afterObjects = [getElementOptions.call(render, container)];
  const operation = new Operation({
    type: DeltaType.MERGE,
    selectable: true,
    preData: {
      objects: preObjects,
    },
    afterData: {
      objects: afterObjects,
    },
    undoHandler: undefined,
    redoHandler: undefined,
  }, render);
  operations.push(operation);
  historyPlugins?.submit(operations);
  deleteLayerData(null, els);
  saveLayerData(null, [container]);
}
/**
 * 拆分组合
 * @export
 * @param {FabricObject[]} [targets]
 * @return {*}
 */
export async function splitGroup(container: Group) {
  const { render, historyPlugins } = useCanvasStore.getState();
  if (!render) return;

  const inTask = hasTaskByLayerId([container._id_]);
  if (inTask) return;
  mtstatTrack('group_operation_click', { type: 1 });
  const preObjects = [getElementOptions.call(render, container)];
  const afterObjects = render.setElementZIndex(container.getObjects().filter((el) => isRealElement(el)).map((el) => getElementOptions.call(render, el)), container);
  const afterShapes = await Promise.all(afterObjects.map(createElement));
  const object = afterShapes.map((el, index) => {
    el.set({
      _parent_id_: '',
    });
    return {
      el,
      index,
    };
  });
  const parentIndex = (container.parent ?? render._FC).getObjects().indexOf(container);
  console.log('parentIndex', parentIndex);
  if (parentIndex === -1) return;

  container.remove(...container.getObjects());
  const parent = container.parent ?? render._FC;
  parent.remove(container);
  // render._FC.add(...afterShapes);
  object.forEach((child) => {
    parent.insertAt(child.index + parentIndex, child.el);
    render._FC.fire('object:added', {
      target: child.el,
    });
  });
  const operation = new Operation({
    type: DeltaType.SPLIT,
    selectable: true,
    preData: {
      objects: preObjects,
    },
    afterData: {
      objects: object.map(({ el }) => getElementOptions.call(render, el)),
    },
    undoHandler: undefined,
    redoHandler: undefined,
  }, render);
  historyPlugins?.submit(operation);
  const as = new ActiveSelection(afterShapes, {
    canvas: render._FC,
  });
  render._FC.setActiveObject(as);
  render._FC.renderAll();
  deleteLayerData(null, [container]);
  const elements = render._FC.getObjects().map((el) => getElementOptions.call(render, el));
  let minIndex = elements.length - 1;
  elements.forEach((el, index) => {
    const target = object.find((item) => item.el._id_ === el._id_);
    if (target) {
      minIndex = Math.min(minIndex, index);
    }
  });
  const syncData = elements.slice(minIndex);
  saveLayerData(null, syncData.map((item) => render.Finder.findById(item._id_)).filter((item) => !(item instanceof Canvas)) as FabricObject[]);
}

// 从画布中复制到剪切板
export async function copyToClipboard(target: FabricObject) {
  try {
    const { render } = useCanvasStore.getState();
    if (target._name_ === ElementName.IMAGE) {
      const { suffix } = target._custom_data_history_;
      const blob = await render?.Actions.exportElementToBlob([target] as [Group], {
        includeType: [ElementName.IMAGE],
        exportType: suffix,
        merge: false,
      });
      if (blob?.[0]) {
        const clipboardItem = new ClipboardItem({
          [blob[0].type]: blob[0],
        });
        await navigator.clipboard.write([clipboardItem]);
      }
    }
  } catch (error) {
    console.log('复制失败', error);
  }
}

/**
 * 复制
 *
 * @export
 * @param {FabricObject[]} targets
 * @return {*}
 */
export async function copy(targets?: FabricObject[]) {
  const els = mergeTargetsActiveObject(targets);
  if (!els) return;
  copyFabricObjectToClipboard(els);
  const { render } = useCanvasStore.getState();
  render?.Actions.copyStash(...els);
}

// 粘贴埋点
function mtstatPaste(elements: FabricObject[]) {
  const importType: string[] = [];
  let videoNum = 0;
  let imageNum = 0;
  let groupNum = 0;
  let textNum = 0;
  const statUrl: string[] = [];
  elements.forEach((element) => {
    if (element._name_ === ElementName.VIDEO) {
      videoNum += 1;
      !importType.includes(ElementName.VIDEO) && importType.push(ElementName.VIDEO);
    } else if (element._name_ === ElementName.IMAGE) {
      imageNum += 1;
      !importType.includes(ElementName.IMAGE) && importType.push(ElementName.IMAGE);
    } else if (element._name_ === ElementName.TEXT) {
      textNum += 1;
      !importType.includes(ElementName.TEXT) && importType.push(ElementName.TEXT);
    } else if (element._name_ === ElementName.CONTAINER) {
      groupNum += 1;
      !importType.includes('group') && importType.push('group');
    }
    statUrl.push(element?._custom_data_history_?.url);
  });
  mtstatTrack('canvas_import', {
    import_path: 3,
    import_type: importType.join(','),
    video_num: videoNum,
    image_num: imageNum,
    group_num: groupNum,
    text_num: textNum,
    url: statUrl.join(','),
  });
}

/**
 * 粘贴
 *
 * @export
 * @param {{ x: number;y: number; }} position
 * @return {*}
 */
export async function paste(position?: { x: number; y: number; }) {
  const { render } = useCanvasStore.getState();
  if (!render) return;
  if (render.Actions.copyElement?.length) {
    // 画布内粘帖
    const pointer = render?.Actions?._copyPoint;
    if (!pointer) return;
    const point = new Point(position || { x: pointer.x + 40, y: pointer.y + 40 });
    const elements = await render.Actions.paste(point, true, false, render._FC);
    if (!elements) return;

    addCopyObjectsToCanvas(elements);
  }
}

/**
 * 拖拽复制
 *
 * @export
 * @param {React.MouseEvent} e
 * @return {*}
 */
export async function dragCopy(e: React.MouseEvent) {
  const { copyObjects } = useCanvasObjectStore.getState();
  const { render } = useCanvasStore.getState();
  const { currentObjects } = useCanvasObjectStore.getState();
  if (!render) return;

  // 如果没有复制对象，则创建新的复制对象
  if (!copyObjects) {
    const elementOptions = currentObjects.map((item) => {
      return getElementOptions.call(render, item);
    });

    const elements = await Promise.all(elementOptions.map(async (el) => {
      const element = await createElement({ ...el, _id_: uuid() });
      return element;
    }));
    setCopyObjects(elements);
  }

  if (copyObjects) {
    const canvasObjects = render._FC.getObjects();
    // 检查复制对象是否已经在画布上
    const objectsToAdd = copyObjects.filter((obj) => !canvasObjects.some((canvasObj) => canvasObj._id_ === obj._id_));

    // 按住Alt键时添加对象，但只添加一次
    if (e.altKey) {
      if (objectsToAdd.length) {
        // 只添加尚未添加到画布的对象
        render._FC.add(...objectsToAdd);
      }
    } else if (!objectsToAdd.length) {
      // 如果没有按Alt键且对象已在画布上，则移除它们
      render._FC.remove(...copyObjects);
    }
  }
}

export async function addCopyObjectsToCanvas(targets: FabricObject[]) {
  const { historyPlugins } = useCanvasStore.getState();
  const operation = historyPlugins?.baseAction.getAddOperation({
    objects: targets,
  });
  if (!operation) return;
  historyPlugins?.submit(operation);
  setSelectCursor();
  saveLayerData(null, targets);
  mtstatPaste(targets); // 埋点
  // 如果粘贴的是文字, 就不让移动
  if (targets.length === 1 && targets[0]._name_ === ElementName.TEXT) return;
  suitableLoaction(targets, 0.4);
}

/**
 * 剪切
 *
 * @export
 * @param {FabricObject[]} targets
 * @return {*}
 */
export async function cut(targets?: FabricObject[]) {
  const { render, historyPlugins } = useCanvasStore.getState();
  const els = mergeTargetsActiveObject(targets);
  if (!els || els.length === 0) return;
  const inTask = hasTaskByLayerId(els.map(({ _id_ }) => _id_));
  if (inTask) return;
  render?.Actions.cut(els);
  const operation = historyPlugins?.baseAction.getRemoveOperation({
    objects: els,
  });
  if (!operation) return;
  historyPlugins?.submit(operation);
  deleteLayerData(null, els);
}
/**
 * 替换图片
 *
 * @export
 * @param {Partial<ImageCustomOptions & ImageOptions>} options
 * @param {FabricObject} target
 * @return {*}
 */
export async function replaceImage({
  target,
  src,
}: {
  target?: FabricObject;
  src: string;
}): Promise<boolean> {
  try {
    const { render } = useCanvasStore.getState();
    const els = mergeTargetsActiveObject();

    if (!render || (!target && (!els || els.length === 0))) {
      return false; // 没有渲染器或没有对象
    }

    const activeTarget = target || (els as FabricObject[])[0];
    await (activeTarget as IImage).setImageSrc(src);

    render._FC.requestRenderAll();
    return true; // 成功替换
  } catch (err) {
    console.error('replaceImage error:', err);
    return false; // 失败
  }
}

/**
 * 批量使用对象的 _custom_data_history_.Url 替换图片
 */
export async function replaceImagesByUrl({ isReplaceWatermark, targets }: { isReplaceWatermark: boolean; targets?: FabricObject[]; }) {
  try {
    const { currentObjects } = useCanvasObjectStore.getState();
    const els = targets && targets.length ? targets : currentObjects;
    if (!els.length) return false;
    const tasks = els.map((t) => {
      const customData = t._custom_data_history_;
      if (!customData) return Promise.resolve(false);
      const { watermarkUrl, url } = customData;
      if (!watermarkUrl) return Promise.resolve(false);
      return replaceImage({ target: t, src: isReplaceWatermark ? watermarkUrl : url });
    });
    await Promise.all(tasks);
    return true;
  } catch (err) {
    console.error('replaceImagesByUrl error:', err);
    return false;
  }
}

/**
 * 全选
 */
export function selectAll() {
  const { render } = useCanvasStore.getState();
  const AS = new ActiveSelection();

  const objs = render?._FC.getObjects().filter((i) => i.selectable);
  if (!objs) return;
  AS.add(...objs);
  render?._FC.setActiveObject(AS);
  render?._FC.renderAll();
}

/**
 * 全不选
 */
export function unSelectAll() {
  const { render } = useCanvasStore.getState();
  render?._FC.discardActiveObject();
  render?._FC.renderAll();
}

/**
 * 删除
 *
 * @export
 * @return {*}
 */
export function remove(objects?: FabricObject[]) {
  const { render, historyPlugins } = useCanvasStore.getState();
  const targets = mergeTargetsActiveObject(objects);
  if (!targets?.length) return;
  const inTask = hasTaskByLayerId(targets.map(({ _id_ }) => _id_));
  if (inTask) return;
  const operation = historyPlugins?.baseAction.getRemoveOperation({
    objects: targets,
  });
  if (!operation) return;
  historyPlugins?.submit(operation);
  render?.Actions.remove(objects);
  deleteLayerData(null, targets);
}

/**
 * 置顶
 *
 * @export
 * @param {FabricObject[]} targets
 * @return {*}
 */
export function bringToFrontAction(els?: FabricObject[]) {
  try {
    const { render, historyPlugins } = useCanvasStore.getState();
    const targets = mergeTargetsActiveObject(els);
    if (!targets || !render) return;
    const inTask = hasTaskByLayerId(targets.map(({ _id_ }) => _id_));
    if (inTask) return;
    const minIndex = getElementOptions.call(render, targets[0]).zIndex || 0;

    const beforeData = targets.map((item) => {
      return getElementOptions.call(render, item);
    });
    render?.Actions.bringToFront(targets);
    const afterData = targets.map((item) => {
      return getElementOptions.call(render, item);
    });
    const operation = historyPlugins?.baseAction.getModifiedOperation({ beforeData, afterData });
    if (!operation) return;
    historyPlugins?.submit(operation);
    saveLayerData(null, render._FC.getObjects().slice(minIndex));
  } catch (error) {
    console.error(error, '置顶');
  }
}

/**
 * 置底
 *
 * @export
 * @param {FabricObject[]} targets
 * @return {*}
 */
export function sendToBackAction(els?: FabricObject[]) {
  try {
    const { render, historyPlugins } = useCanvasStore.getState();
    const targets = mergeTargetsActiveObject(els);
    if (!targets || !render) return;
    const inTask = hasTaskByLayerId(targets.map(({ _id_ }) => _id_));
    if (inTask) return;
    let maxIndex = getElementOptions.call(render, targets[0]).zIndex || 0;

    const beforeData = targets.map((item) => {
      return getElementOptions.call(render, item);
    });
    beforeData.forEach((item) => {
      maxIndex = Math.max(item.zIndex || 0, maxIndex);
    });
    render?.Actions.sendToBack(targets);
    const afterData = targets.map((item) => {
      return getElementOptions.call(render, item);
    });
    const operation = historyPlugins?.baseAction.getModifiedOperation({ beforeData, afterData });
    if (!operation) return;
    historyPlugins?.submit(operation);
    saveLayerData(null, render._FC.getObjects().slice(0, maxIndex + 1));
  } catch (error) {
    console.log(error, '置底');
  }
}

/**
 * 前移一层
 */
export function moveElementForwardOneLayer() {
  const { render, historyPlugins } = useCanvasStore.getState();
  const objects = render?._FC.getActiveObjects() ?? [];
  if (objects.length > 1 || !render) return;
  console.log(objects, historyPlugins, '前进');
  const target = objects[0];
  const inTask = hasTaskByLayerId([target._id_]);
  if (inTask) return;
  const beforeIndex = getElementOptions.call(render, target).zIndex || 0;
  const targetOptions = getElementOptions.call(render, target);
  const afterIndex = beforeIndex + 1;
  render?.Actions.sendZIndex(target, afterIndex);
  const afterOptions = getElementOptions.call(render, target);
  const operation = historyPlugins?.baseAction.getModifiedOperation({
    beforeData: [targetOptions],
    afterData: [afterOptions],
  });
  if (!operation) return;
  historyPlugins?.submit(operation);
  const minIndex = Math.min(afterOptions.zIndex || 0, beforeIndex);
  const maxIndex = Math.max(afterOptions.zIndex || 0, beforeIndex);
  console.log('minIndex', minIndex, 'maxIndex', maxIndex, render._FC.getObjects().slice(minIndex, maxIndex + 1).map((e) => getElementOptions.call(render, e)));
  saveLayerData(null, render._FC.getObjects().slice(minIndex, maxIndex + 1));
}

/**
 * 后移一层
 */
export function moveElementBackwardOneLayer() {
  const { render, historyPlugins } = useCanvasStore.getState();
  const objects = render?._FC.getActiveObjects() ?? [];
  if (objects.length > 1 || !render) return;
  console.log(objects, historyPlugins, '前进');
  const target = objects[0];
  const inTask = hasTaskByLayerId([target._id_]);
  if (inTask) return;
  const beforeIndex = getElementOptions.call(render, target).zIndex || 0;
  const targetOptions = getElementOptions.call(render, target);
  const afterIndex = beforeIndex - 1;
  render?.Actions.sendZIndex(target, afterIndex);
  const afterOptions = getElementOptions.call(render, target);
  const operation = historyPlugins?.baseAction.getModifiedOperation({
    beforeData: [targetOptions],
    afterData: [afterOptions],
  });
  if (!operation) return;
  historyPlugins?.submit(operation);
  const minIndex = Math.min(afterOptions.zIndex || 0, beforeIndex);
  const maxIndex = Math.max(afterOptions.zIndex || 0, beforeIndex);
  console.log('minIndex', minIndex, 'maxIndex', maxIndex, render._FC.getObjects().slice(minIndex, maxIndex + 1).map((e) => getElementOptions.call(render, e)));
  saveLayerData(null, render._FC.getObjects().slice(minIndex, maxIndex + 1));
}

/**
 * 合适的位置
 *
 * @export
 * @param {FabricObject[]} targets
 * @param {number} [proportion=0.6]
 * @return {*}
 */
export function suitableLoaction(targets: FabricObject[], proportion: number = 0.6) {
  const isText = targets.length === 1 && targets[0]._name_ === ElementName.TEXT;
  const remainProportion = (1 - proportion) / 2;
  const { render } = useCanvasStore.getState();
  if (!render) return;
  const { sideBarWidth, sideBarCollapsed } = useChatStore.getState();
  const boundingRect = getShapePositionByScreenBound.call(render, ...targets);
  const boundingRatio = (boundingRect?.height || 0) / (boundingRect?.width || 1);
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;
  const sideBarLeft = sideBarCollapsed ? sideBarWidth : 0;
  // 计算比例时要减去 左侧对话框的宽度
  const windowRatio = windowHeight / (windowWidth - sideBarLeft);
  const limit = isText ? 0.25 : 0.01;
  if (boundingRatio > windowRatio) {
    const h = windowHeight * remainProportion;
    showAllObjects(render._FC, targets, limit, sideBarLeft, 0, h, h, 500, 0);
  } else {
    const w = (windowWidth - sideBarLeft) * remainProportion;
    showAllObjects(render._FC, targets, limit, w + sideBarLeft, w, 0, 0, 500, 0);
  }
}

/**
 * 设置对齐方式
 *
 * @export
 * @param {AlignMode} mode
 * @return {*}
 */
export function setAligeMode(mode: AlignMode) {
  const { render } = useCanvasStore.getState();
  const { currentObjects } = useCanvasObjectStore.getState();

  // 设置对齐时选中的元素要大于1
  if (!(currentObjects.length > 1)) return;
  const searchParams = getUrlSearchParams();
  const room_id = searchParams.get('room_id');
  mtstatTrack('canvas_alignment_click', {
    type: mode,
    room_id,
    element_count: currentObjects.length,
  });

  const inTask = hasTaskByLayerId(currentObjects.map(({ _id_ }) => _id_));
  if (inTask) return;

  submitModifyHistory(() => {
    render?.Actions.alignMode(mode);
  });
  saveLayerData(null, currentObjects);
}

/**
 * 撤销、后退
 * @export
 */
export function undo() {
  const { historyPlugins } = useCanvasStore.getState();
  const searchParams = getUrlSearchParams();
  const room_id = searchParams.get('room_id');
  room_id && mtstatTrack('step_navigation_click', {
    direction: 0,
    room_id,
  });
  historyPlugins?.undo();
}

/**
 * 重试、前进
 *
 * @export
 */
export function redo() {
  const { historyPlugins } = useCanvasStore.getState();
  const searchParams = getUrlSearchParams();
  const room_id = searchParams.get('room_id');
  room_id && mtstatTrack('step_navigation_click', {
    direction: 1,
    room_id,
  });
  historyPlugins?.redo();
}

type MediaItem =
  | {
    type: 'image';
    url: string;
    coverUrl?: string; // 可选
    /** 资产库 id，写入节点 _custom_data_history_.extraData */
    asset_id?: number;
  }
  | {
    type: 'video';
    url: string;
    coverUrl: string; // 必须存在
    asset_id?: number;
  }
  | {
    type: 'audio';
    url: string;
    lyrics?: string;
    name?: string; // 音频名称
    suffix?: string; // 音频后缀
    asset_id?: number;
  };

interface BatchInsertParams {
  insertMedias: MediaItem[];
  target?: FabricObject;
  width?: number; // 图片宽度
  height?: number; // 图片高度
  coverUrl?: string; // 视频封面
  extraData?: Record<string, any>;
}

export async function batchInsert({
  insertMedias,
  target,
  width,
  height,
  extraData,
}: BatchInsertParams): Promise<FabricObject[]> {
  const elsArray: FabricObject[] = [];
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < insertMedias.length; i++) {
    try {
      const insertMedia = insertMedias[i];
      const mergedExtra = {
        ...extraData,
        ...(insertMedia.asset_id != null ? { asset_id: insertMedia.asset_id } : {}),
      };
      if (insertMedia.type === 'image') {
        // eslint-disable-next-line no-await-in-loop
        const imageElement = await insertImage({
          left: target ? (target.left - target.width / 2) : undefined,
          top: target ? (target.top - target.height / 2) : undefined,
          url: insertMedia.url,
          uri: '',
          insertPos: (i === 0 && !target) ? 'leftBottom' : 'right',
          width,
          height,
          extraData: mergedExtra,
        });
        elsArray.push(imageElement);
      }
      if (insertMedia.type === 'video') {
        // eslint-disable-next-line no-await-in-loop
        const videoElement = await insertVideo({
          left: target ? (target.left - target.width / 2) : undefined,
          top: target ? (target.top - target.height / 2) : undefined,
          url: insertMedia.url,
          uri: '',
          coverUrl: insertMedia.coverUrl,
          insertPos: (i === 0 && !target) ? 'leftBottom' : 'right',
          width,
          height,
          extraData: mergedExtra,
        });
        elsArray.push(videoElement);
      }
      if (insertMedia.type === 'audio') {
        // 计算音频插入位置
        let audioLeft: number | undefined;
        let audioTop: number | undefined;

        if (target) {
          audioLeft = target.left - target.width / 2;
          audioTop = target.top - target.height / 2;
        } else if (elsArray.length > 0) {
          const lastElement = elsArray[elsArray.length - 1];
          audioLeft = lastElement.left - lastElement.width / 2;
          audioTop = lastElement.top - lastElement.height / 2;
        }

        // eslint-disable-next-line no-await-in-loop
        const audioElement = await insertAudio({
          left: audioLeft,
          top: audioTop,
          url: insertMedia.url,
          lyrics: insertMedia.lyrics,
          uri: '',
          name: insertMedia.name,
          suffix: insertMedia.suffix,
          insertPos: (i === 0 && !target) ? 'leftBottom' : 'right',
          width,
          height,
          extraData,
        });
        elsArray.push(audioElement);
      }
    } catch (error) {
      console.error('批量插入元素失败:', error);
    }
  }
  return elsArray;
}

/**
 * 画布所有元素的边界点
 */
export const allElementsBounds = () => {
  const { render } = useCanvasStore.getState();
  render?._FC.getObjects().filter(isRealElement).map((element) => {
    return {
      left: Math.min(...element.getCoords().map((coord) => coord.x)),
      top: Math.min(...element.getCoords().map((coord) => coord.y)),
      right: Math.max(...element.getCoords().map((coord) => coord.x)),
      bottom: Math.max(...element.getCoords().map((coord) => coord.y)),
    };
  });
};

/**
 * 创建图片元素
 * @param parent 父级元素
 * @param params 图片参数
 * @returns 图片元素
 */
export const createImageAsync = (
  parentId: string,
  params: Partial<ImageProps> & Record<string, any>,
): Group => {
  const image = document.createElement('img');
  image.crossOrigin = 'anonymous';
  image.src = params.src;

  const shape = new IImage({
    ...ImageProperties,
    ...params,
    _id_: params._id_ || uuid(),
    _parent_id_: parentId,
    _shape_name_: params._shape_name_ || '',
    width: params.width || 100, // 设置默认尺寸
    height: params.height || 100,
    scaleX: params.scaleX || 1,
    scaleY: params.scaleY || 1,
    flipX: params.flipX || false,
    flipY: params.flipY || false,
    _z_index_: params.zIndex || 0,
    image,
    src: params.src,
    backgroundColor: '#fff',
    // secondaryElements: [], // 确保传递次要元素
  });

  // 异步加载真实图片
  FabricImage.fromURL(params.src, {
    crossOrigin: 'anonymous',
  }).then((img: FabricImage) => {
    if (img.getElement()) {
      shape.fabricImage.setElement(img.getElement());
      shape.dirty = true;
      shape.loaded = true;
      shape.canvas?.fire('object:loaded', { target: shape });
      shape.backgroundColor = 'transparent';
      shape.setCoords();
      if (!params.width) shape.set('width', img.width);
      if (!params.height) shape.set('height', img.height);
      const canvas = shape.canvas || shape.group?.canvas;
      if (canvas) {
        canvas.renderAll();
      }
    }
  });

  return shape;
};

/**
* 创建视频元素
* @param parent 父级元素
* @param params 视频参数
* @returns 视频元素
*/
export const createVideoAsync = (parentId: string, params: Record<string, any>) => {
  const image = document.createElement('img');
  image.crossOrigin = 'anonymous';
  image.src = params.cover;
  const shape = new IVideo({
    ...VideoProperties,
    ...params,
    _id_: params._id_ || uuid(),
    _shape_name_: params._shape_name_ || '',
    _parent_id_: parentId,
    width: params.width || 0,
    height: params.height || 0,
    scaleX: params.scaleX || 1,
    scaleY: params.scaleY || 1,
    flipX: params.flipX || false,
    flipY: params.flipY || false,
    _z_index_: params.zIndex || 0,
    cover: image,
    source: params.source,
    backgroundColor: '#fff',
  });
  FabricImage.fromURL(params.cover, { crossOrigin: 'anonymous' }).then((img) => {
    if (img.getElement()) {
      shape.fabricImage.setElement(img.getElement());
      shape.dirty = true;
      shape.loaded = true;
      shape.canvas?.fire('object:loaded', { target: shape });
      shape.setCoords();
      shape.backgroundColor = 'transparent';
      if (!params.width) shape.set('width', img.width);
      if (!params.height) shape.set('height', img.height);
      const canvas = shape.canvas || shape.group?.canvas;
      if (canvas) canvas.renderAll();
    }
  });
  return shape;
};

/**
* 创建动态图片元素
* @param parent 父级元素
* @param params 动态图片参数
* @returns 动态图片元素
*/
export const createDynamicPictureAsync = (parentId: string, params: Record<string, any>) => {
  const image = document.createElement('img');
  image.crossOrigin = 'anonymous';
  image.src = params.source;
  const shape = new IDynamicPicture({
    ...DynamicPictureProperties,
    ...params,
    _id_: params._id_ || uuid(),
    _shape_name_: params._shape_name_ || '',
    _parent_id_: parentId,
    width: params.width || 0,
    height: params.height || 0,
    scaleX: params.scaleX || 1,
    scaleY: params.scaleY || 1,
    flipX: params.flipX || false,
    flipY: params.flipY || false,
    _z_index_: params.zIndex || 0,
    cover: image,
    source: params.source,
  });
  FabricImage.fromURL(params.source, { crossOrigin: 'anonymous' }).then((img) => {
    if (img.getElement()) {
      shape.fabricImage.setElement(img.getElement());
      shape.dirty = true;
      shape.loaded = true;
      shape.canvas?.fire('object:loaded', { target: shape });
      shape.backgroundColor = 'transparent';
      shape.setCoords();
      if (!params.width) shape.set('width', img.width);
      if (!params.height) shape.set('height', img.height);
      const canvas = shape.canvas || shape.group?.canvas;
      if (canvas) canvas.renderAll();
    }
  });
  return shape;
};
